// hooks/useAcademicPlans.ts
/**
 * Hook to manage multiple academic plans.
 * Migrated to use typedApi for type safety.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { useApp } from '../contexts/AppContext';
import { useActiveInstitution } from '../contexts/ActiveInstitutionContext';
import { typedApi } from '../services/typedApiClient';
import type { AcademicPlan, ProgramSelection } from '../types';
import { getErrorMessage } from '../utils/errorUtils';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Plan data for creating a new plan
 */
export interface CreatePlanData {
  plan_type?: string;
  majors?: ProgramSelection[];
  minors?: ProgramSelection[];
  copy_transfer_credits?: boolean;
}

/**
 * Plan updates for updating programs
 */
export interface PlanUpdates {
  majors?: ProgramSelection[];
  minors?: ProgramSelection[];
  citations?: ProgramSelection[];
  [key: string]: unknown;
}

/**
 * Copy type options
 */
export type CopyType = 'all' | 'completed_only';

/**
 * Return type for the useAcademicPlans hook
 */
export interface UseAcademicPlansReturn {
  plans: AcademicPlan[];
  loading: boolean;
  error: string | null;
  createPlan: (planData: CreatePlanData) => Promise<AcademicPlan>;
  clearPlan: (planId: number) => Promise<unknown>;
  copyPlan: (fromPlanId: number, toPlanId: number, copyType?: CopyType) => Promise<unknown>;
  setActivePlan: (planId: number) => Promise<void>;
  updatePlan: (planId: number, updates: PlanUpdates) => Promise<void>;
  renamePlan: (planId: number, newName: string) => Promise<void>;
  getActivePlan: () => AcademicPlan | null;
  getPlanById: (planId: number) => AcademicPlan | null;
  refreshPlans: () => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to manage multiple academic plans
 */
export const useAcademicPlans = (): UseAcademicPlansReturn => {
  const { user } = useAuth();
  const { syncingUser } = useApp();
  // Plans are scoped per-(user, institution) server-side, so the active
  // institution is a fetch key: switching schools must rescope the plan list.
  const { activeSlug } = useActiveInstitution();
  const [plans, setPlans] = useState<AcademicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Once plans have loaded, never set loading=true again (prevents full-page spinner).
  const hasEverLoaded = useRef(false);

  // Fetch all plans for the user
  const fetchPlans = useCallback(async (): Promise<void> => {
    try {
      // Only show loading state on initial load — subsequent fetches are silent
      if (!hasEverLoaded.current) {
        setLoading(true);
      }
      setError(null);

      // REQUIRE authentication - no anonymous plan support
      if (!user?.id) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const data = await typedApi.academicPlans.list();
      const plansData = data as { plans?: AcademicPlan[] };
      setPlans(plansData.plans || []);
      hasEverLoaded.current = true;
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Track previous user ID / active institution to know when to clear + rescope.
  const prevUserIdRef = useRef<string | undefined>(user?.id);
  const prevActiveSlugRef = useRef<string | null>(activeSlug);

  // Refetch when the user OR the active institution changes. Plans are scoped
  // per-(user, institution), so a school switch must rescope the list; the GET
  // also self-heals a plan-less institution via ensure_three_plans (backend).
  // Wait for user sync to complete before fetching to avoid a race condition.
  useEffect(() => {
    // Clear the stale list when the user changes (prevents blank-page flashes on
    // token refresh) OR when the active institution changes — otherwise the
    // switcher keeps showing the previous school's plans until a manual reload.
    if (user?.id !== prevUserIdRef.current || activeSlug !== prevActiveSlugRef.current) {
      setPlans([]);
      hasEverLoaded.current = false;
      setError(null);
      prevUserIdRef.current = user?.id;
      prevActiveSlugRef.current = activeSlug;
    }

    // Don't fetch until sync is complete - prevents 401/404 errors for new users
    if (syncingUser) {
      setLoading(true);
      return;
    }

    fetchPlans();
  }, [fetchPlans, syncingUser, user?.id, activeSlug]);

  // Create a new plan
  const createPlan = async (planData: CreatePlanData): Promise<AcademicPlan> => {
    try {
      // REQUIRE authentication
      if (!user?.id) {
        throw new Error('Authentication required to create a plan');
      }

      // Note: Type assertion via unknown needed as the API schema differs from actual usage
      const data = await typedApi.academicPlans.create({
        plan_type: planData.plan_type || 'alternate',
        majors: planData.majors || [],
        minors: planData.minors || [],
        copy_transfer_credits: planData.copy_transfer_credits || false
      } as unknown as Parameters<typeof typedApi.academicPlans.create>[0]);

      // Refresh plans list
      await fetchPlans();

      const response = data as unknown as { plan: AcademicPlan };
      return response.plan;
    } catch (err) {
      console.error('Error creating plan:', err);
      throw err;
    }
  };

  // Clear a plan - removes all courses but keeps plan structure and majors/minors
  const clearPlan = async (planId: number): Promise<unknown> => {
    try {
      if (!user?.id) {
        throw new Error('Authentication required');
      }

      const result = await typedApi.academicPlans.clear(planId);

      // Refresh plans list to update UI
      await fetchPlans();

      return result;
    } catch (err) {
      console.error('Error clearing plan:', err);
      throw err;
    }
  };

  // Copy from one plan to another
  const copyPlan = async (fromPlanId: number, toPlanId: number, copyType: CopyType = 'all'): Promise<unknown> => {
    try {
      if (!user?.id) {
        throw new Error('Authentication required');
      }

      const result = await typedApi.academicPlans.copy(toPlanId, {
        from_plan_id: fromPlanId,
        copy_type: copyType // 'all' or 'completed_only'
      });

      // Refresh plans list to update UI
      await fetchPlans();

      return result;
    } catch (err) {
      console.error('Error copying plan:', err);
      throw err;
    }
  };

  // Set a plan as active
  const setActivePlan = async (planId: number): Promise<void> => {
    try {
      // REQUIRE authentication
      if (!user?.id) {
        throw new Error('Authentication required');
      }

      await typedApi.academicPlans.setActive(planId);

      // Update local state
      setPlans(prevPlans =>
        prevPlans.map(plan => ({
          ...plan,
          is_active: plan.id === planId
        }))
      );
    } catch (err) {
      console.error('Error setting active plan:', err);
      throw err;
    }
  };

  // Update plan metadata
  const updatePlan = async (planId: number, updates: PlanUpdates): Promise<void> => {
    try {
      // REQUIRE authentication
      if (!user?.id) {
        throw new Error('Authentication required');
      }

      // Note: Type assertion needed as the API schema differs from actual usage
      await typedApi.academicPlans.updatePrograms(planId, updates as Parameters<typeof typedApi.academicPlans.updatePrograms>[1]);

      // Refresh plans list
      await fetchPlans();
    } catch (err) {
      console.error('Error updating plan:', err);
      throw err;
    }
  };

  // Rename a plan
  const renamePlan = async (planId: number, newName: string): Promise<void> => {
    try {
      // REQUIRE authentication
      if (!user?.id) {
        throw new Error('Authentication required');
      }

      await typedApi.academicPlans.rename(planId, newName);

      // Update local state
      setPlans(prevPlans =>
        prevPlans.map(plan =>
          plan.id === planId ? { ...plan, plan_name: newName } : plan
        )
      );
    } catch (err) {
      console.error('Error renaming plan:', err);
      throw err;
    }
  };

  // Get the active plan
  const getActivePlan = (): AcademicPlan | null => {
    return plans.find(plan => plan.is_active) || plans[0] || null;
  };

  // Get plan by ID
  const getPlanById = (planId: number): AcademicPlan | null => {
    return plans.find(plan => plan.id === planId) || null;
  };

  return {
    plans,
    loading,
    error,
    createPlan,
    clearPlan,
    copyPlan,
    setActivePlan,
    updatePlan,
    renamePlan,
    getActivePlan,
    getPlanById,
    refreshPlans: fetchPlans
  };
};
